import { useCallback } from 'react';
import { toLowerCamelCase } from 'utils/string';
import { hasCalculateBehavior } from 'utils/userAgendaTableTypeGuard';
import { VISIBILITY_STATE } from 'types/enums';
import type { FormItemDefinition, VisibilityResult } from 'types';
import type { UserAgendaTableDataSchemaType, ValidationResult } from 'types/userAgendaTable';

export const useAgendaTableValidation = (dataSchema: UserAgendaTableDataSchemaType, visibilityResults?: Record<string, VisibilityResult>) => {
  const checkCalculateRequiredFields = useCallback(
    (rowId: string, formData: Record<string, unknown> | undefined): ValidationResult => {
      if (!formData || !formData[rowId]) {
        return { allFilled: true, missingFields: [] };
      }

      const missingFields: string[] = [];
      const rowData = formData[rowId] as Record<string, unknown>;

      Object.keys(dataSchema).forEach((columnId) => {
        const schemaDef = dataSchema[columnId];
        const formItemDef = schemaDef?.formItemDefinition;

        if (!formItemDef) return;

        const defKey = toLowerCamelCase(formItemDef.type) as keyof FormItemDefinition;

        if (hasCalculateBehavior(formItemDef, defKey)) {
          if (visibilityResults?.[`${rowId}.${columnId}`]?.visibilityState === VISIBILITY_STATE.HIDDEN) return;

          const fieldValue = rowData[columnId];

          if (fieldValue === undefined || fieldValue === null || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
            missingFields.push(columnId);
          }
        }
      });

      return {
        allFilled: missingFields.length === 0,
        missingFields,
      };
    },
    [dataSchema, visibilityResults]
  );

  const highlightMissingFields = useCallback((rowId: string, missingFields: string[], t: (key: string) => string): void => {
    missingFields.forEach((fieldId) => {
      const fieldName = `${rowId}.${fieldId}`;
      const element = document.querySelector(`[name="${fieldName}"]`) as HTMLInputElement | HTMLTextAreaElement | null;

      if (element) {
        element.title = t('flow.modulesAndAgendas.form.item.calculate.fieldRequired');
      }
    });
  }, []);

  return {
    checkCalculateRequiredFields,
    highlightMissingFields,
  };
};
